import { Button, Input, Modal, Popover, Slider, Tour, message } from 'antd';
import type { TourProps } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { downloadAllPlanchesPdfZip, isDownloadUnlocked, fetchAdminDownload, getJobName, setJobName, PlancheImageError, FEATURE_DOWNLOAD_TEST } from './exportPlanche';
import { importDTFFile, ImportError } from './importFile';
import ItemsPanel from './ItemsPanel';
import { recolorSvg, svgToDataUrl, recolorRaster } from './recolor';
import { nestItems } from './nesting';
import { DTFItem, PackResult } from './packing';
import PlancheView from './PlancheView';
import { computePrice, fmtMoney } from './pricing';
import { DTFSettings, loadSettings, saveSettings } from './settings';
import SettingsModal from './SettingsModal';
import { effectiveDpi, hasThinStrokes } from './thinlines';
import { fixThinStrokes, ThinFixMode } from './thinfix';
import { analyzeQuality, fmtCm, QualityInfo } from './quality';
import { computeLogoMetrics, logLogoMetrics, LogoMetrics } from './logoMetrics';
import { vectorizeImage, VectorizeAuthError, VectorizeQuotaError } from './vectorize';
import { importDTFFile as importDTFFileForVecto } from './importFile';
import QualityModal from './QualityModal';
import { upscaleImage, UpscaleMaxedError } from './upscale';
import { smoothEdges } from './smooth';
import BgRemoveModal from './BgRemoveModal';
import PrepWizard from './PrepWizard';
import { loadItems, saveItems, savePendingOrder } from './persist';
import LibraryModal from './LibraryModal';
import SiteTopbar from './SiteTopbar';
import { adminPrintUrl, composeBoards, recolorLibraryLogo, ensureItemBlobs, fetchToDataUrl, isLoggedIn, LibraryAuthError, LibraryBoardDetail, LibraryLogo, listLogos, logoPreview, saveLogoToLibrary, smallThumb } from './library';
import { ORDER_PAGE } from './exportPlanche';

const MIN_DPI = 150;
import TextModal, { TextEntry } from './TextModal';
import Marmotte from './Marmotte';
import { TutoHost, TutoLink, TutoList } from './Tutos';
import './styles/dtf.css';

const PX_PER_CM = 8;
const round1 = (v: number) => Math.round(v * 10) / 10;

/** Animations désactivées : préférence système OU harnais E2E (flag semé par les suites). */
const noAnim = () =>
	(!!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
	localStorage.getItem('dtf_v2_no_autoinspect') === '1';

/** Compteur animé : la valeur affichée « roule » vers la nouvelle (prix, longueur). */
const CountUp: React.FC<{ value: number; format: (n: number) => string }> = ({ value, format }) => {
	const [disp, setDisp] = useState(value);
	const from = useRef(value);
	useEffect(() => {
		const start = from.current;
		from.current = value;
		if (start === value) return undefined;
		if (noAnim()) {
			setDisp(value);
			return undefined;
		}
		let raf = 0;
		const t0 = performance.now();
		const dur = 420;
		const tick = (t: number) => {
			const p = Math.min(1, (t - t0) / dur);
			const e = 1 - (1 - p) ** 3;
			setDisp(start + (value - start) * e);
			if (p < 1) raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [value]);
	return <>{format(disp)}</>;
};

/** « logo.png » déjà présent → « logo.png (2) », (3)… */
const dedupName = (name: string, names: string[]) => {
	if (!names.includes(name)) return name;
	let i = 2;
	while (names.includes(`${name} (${i})`)) i++;
	return `${name} (${i})`;
};

/** « logo (copie) » déjà présent → « logo (copie 2) », (copie 3)… */
const copyName = (base: string, names: string[]) => {
	const suffix = i18next.t('dtf.copie-suffixe') as string;
	let n = `${base}${suffix}`;
	let i = 2;
	while (names.includes(n)) {
		n = `${base}${suffix.replace(')', ` ${i})`)}`;
		i++;
	}
	return n;
};

const DTFEditor: React.FC = () => {
	const [settings, setSettings] = useState<DTFSettings>(() => loadSettings());
	const [items, setItems] = useState<DTFItem[]>([]);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [textOpen, setTextOpen] = useState(false);
	const [textEditId, setTextEditId] = useState<string | null>(null);
	const [boardDark, setBoardDark] = useState(() => localStorage.getItem('dtf_v2_board_dark') === '1');
	const fileRef = useRef<HTMLInputElement>(null);
	const wsRef = useRef<HTMLElement>(null);

	// ---- Zoom (Ctrl+molette / pinch trackpad + boutons) ----
	const [zoom, setZoom] = useState(1);
	const zoomBy = (f: number) => setZoom(z => Math.min(3, Math.max(0.4, Math.round(z * f * 100) / 100)));
	useEffect(() => {
		const el = wsRef.current;
		if (!el) return undefined;
		const onWheel = (e: WheelEvent) => {
			if (!e.ctrlKey) return;
			e.preventDefault();
			const rect = el.getBoundingClientRect();
			const cx = e.clientX - rect.left;
			const cy = e.clientY - rect.top;
			const sl = el.scrollLeft;
			const st = el.scrollTop;
			setZoom(z => {
				const nz = Math.min(3, Math.max(0.4, Math.round(z * (e.deltaY < 0 ? 1.15 : 1 / 1.15) * 100) / 100));
				if (nz !== z) {
					// garder (approximativement) le point sous le curseur en place
					requestAnimationFrame(() => {
						el.scrollLeft = (sl + cx) * (nz / z) - cx;
						el.scrollTop = (st + cy) * (nz / z) - cy;
					});
				}
				return nz;
			});
		};
		el.addEventListener('wheel', onWheel, { passive: false });
		return () => el.removeEventListener('wheel', onWheel);
	}, []);

	// ---- Pan : glisser la zone planches pour se déplacer ----
	const panState = useRef<{ x: number; y: number; sl: number; st: number; id: number } | null>(null);
	const panMoved = useRef(false);
	const [panning, setPanning] = useState(false);
	const onWsPointerDown = (e: React.PointerEvent) => {
		if (e.button !== 0) return;
		const el = wsRef.current;
		if (!el) return;
		panState.current = { x: e.clientX, y: e.clientY, sl: el.scrollLeft, st: el.scrollTop, id: e.pointerId };
		panMoved.current = false;
	};
	const onWsPointerMove = (e: React.PointerEvent) => {
		const s = panState.current;
		const el = wsRef.current;
		if (!s || !el || e.pointerId !== s.id) return;
		const dx = e.clientX - s.x;
		const dy = e.clientY - s.y;
		if (!panMoved.current && Math.abs(dx) + Math.abs(dy) > 6) {
			panMoved.current = true;
			setPanning(true);
		}
		if (panMoved.current) {
			el.scrollLeft = s.sl - dx;
			el.scrollTop = s.st - dy;
		}
	};
	const onWsPointerEnd = () => {
		panState.current = null;
		setPanning(false);
		// panMoved reste vrai jusqu'au prochain pointerdown : il supprime le clic de fin de pan
	};

	// ---- Sélection croisée planche ↔ cartes ----
	const [selectedId, setSelectedId] = useState<string | null>(null);
	// carte à ouvrir dans le panneau « Mes logos » (import, fermeture de l'assistant) — distinct de la sélection sur la planche
	const [cardOpenId, setCardOpenId] = useState<string | null>(null);
	const [hoverId, setHoverId] = useState<string | null>(null);
	const selectFromBoard = (id: string | null) => {
		if (panMoved.current) return;
		setSelectedId(id);
		if (id) {
			const card = document.querySelector(`.dtf-item-card[data-item="${id}"]`);
			if (card) card.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
		}
	};
	const selectFromCard = (id: string) => {
		setSelectedId(id);
		const box = document.querySelector(`.dtf-placement-box[data-item="${id}"]`);
		if (box) box.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' });
	};

	// ---- Outil mesure ----
	const [measureMode, setMeasureMode] = useState(false);

	// ---- Historique (Ctrl+Z / Ctrl+Maj+Z) ----
	const past = useRef<DTFItem[][]>([]);
	const future = useRef<DTFItem[][]>([]);
	const prevItems = useRef<DTFItem[]>(items);
	const skipHist = useRef(false);
	const [, setHistTick] = useState(0);
	useEffect(() => {
		if (prevItems.current === items) return;
		if (skipHist.current) skipHist.current = false;
		else {
			past.current.push(prevItems.current);
			if (past.current.length > 100) past.current.shift();
			future.current = [];
		}
		prevItems.current = items;
		setHistTick(t => t + 1);
	}, [items]);
	const undo = () => {
		const p = past.current.pop();
		if (!p) {
			message.info(i18next.t('dtf.undo-rien') as string, 3);
			return;
		}
		future.current.push(prevItems.current);
		skipHist.current = true;
		setItems(p);
	};
	const redo = () => {
		const f = future.current.pop();
		if (!f) {
			message.info(i18next.t('dtf.redo-rien') as string, 3);
			return;
		}
		past.current.push(prevItems.current);
		skipHist.current = true;
		setItems(f);
	};
	const [orderBusy, setOrderBusy] = useState(false);
	// #224d (Daniel 23.09) : « Exporter tout » = un ZIP avec un PDF par planche, visible seulement avec le déverrouillage
	const [zipBusy, setZipBusy] = useState(false);
	// #224e : nom de la préparation (client / commande) → placé dans les noms de fichiers exportés
	const [jobName, setJobNameState] = useState<string>(() => getJobName());
	const onJobName = (v: string) => {
		setJobNameState(v);
		setJobName(v);
	};
	/** Bouton « PDF » d'une planche (admin) : composition côté serveur, puis téléchargement de cette planche seulement. */
	const downloadPlancheViaServer = async (index: number) => {
		if (!isLoggedIn()) {
			message.warning(i18next.t('dtf.export-connexion') as string, 8);
			return;
		}
		const hide = message.loading(i18next.t('dtf.export-serveur-en-cours') as string, 0);
		try {
			const boards = await freezeBoards('admin', colle || 'base');
			const b = boards[index];
			if (!b) throw new Error('planche introuvable');
			const { url, filename } = await adminPrintUrl(b.board_version_id);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.rel = 'noopener';
			document.body.appendChild(a);
			a.click();
			a.remove();
			hide();
		} catch (e) {
			hide();
			message.destroy('lib-up');
			if (e instanceof LibraryAuthError) message.warning(i18next.t('dtf.export-connexion') as string, 8);
			else message.error(`${i18next.t('dtf.export-serveur-echec')} ${(e as Error)?.message || ''}`, 8);
		}
	};

	const onExportAllPdf = async () => {
		if (!pack.planches.length || zipBusy) return;
		setZipBusy(true);
		try {
			if (FEATURE_DOWNLOAD_TEST) {
				// localhost + ?telecharger=1 : ancien export navigateur, réservé aux suites de test historiques
				await downloadAllPlanchesPdfZip(pack.planches, items, settings);
				return;
			}
			// Daniel 15.10.2026 : « je veux que ce soit vectoriel » — un logo repris de la bibliothèque n'est qu'un aperçu dans le navigateur ;
			// le fichier d'impression se compose CÔTÉ SERVEUR à partir des vrais fichiers (SVG restés vectoriels), puis se télécharge (admin, journalisé)
			if (!isLoggedIn()) {
				message.warning(i18next.t('dtf.export-connexion') as string, 8);
				return;
			}
			const hide = message.loading(i18next.t('dtf.export-serveur-en-cours') as string, 0);
			try {
				const boards = await freezeBoards('admin', colle || 'base');
				for (const b of boards) {
					const { url, filename } = await adminPrintUrl(b.board_version_id);
					const a = document.createElement('a');
					a.href = url;
					a.download = filename;
					a.rel = 'noopener';
					document.body.appendChild(a);
					a.click();
					a.remove();
					await new Promise(r => setTimeout(r, 600));
				}
				hide();
				message.success(i18next.t('dtf.export-serveur-ok', { n: boards.length }) as string, 6);
			} catch (e) {
				hide();
				message.destroy('lib-up');
				if (e instanceof LibraryAuthError) message.warning(i18next.t('dtf.export-connexion') as string, 8);
				else message.error(`${i18next.t('dtf.export-serveur-echec')} ${(e as Error)?.message || ''}`, 8);
			}
		} catch (e) {
			message.error(i18next.t('dtf.err-import-generique', { nom: 'ZIP' }) as string);
		} finally {
			setZipBusy(false);
		}
	};
	/* #223 : fin de parcours = commande (pas d'export). Les PDF sont générés en mémoire,
	   déposés dans IndexedDB et récupérés par /commande-dtfs.html qui les met au panier. */
	// Daniel 09.10.2026 : le type de colle se choisit ICI, avant « Préparer ma commande » (pas de valeur par défaut :
	// le client doit choisir), il change le prix affiché (+50 % anti-sublimation) et suit la planche jusqu'à la commande.
	const [colle, setColle] = useState<'base' | 'anti' | null>(() => {
		try { const v = localStorage.getItem('dtf_v2_colle'); return v === 'base' || v === 'anti' ? v : null; } catch (_e) { return null; }
	});
	const [colleFlash, setColleFlash] = useState(false);
	const chooseColle = (g: 'base' | 'anti') => {
		setColle(g);
		setColleFlash(false);
		try { localStorage.setItem('dtf_v2_colle', g); } catch (_e) { /* ignore */ }
	};
	// section 1 du panneau (maquette v3.3) : on y renvoie depuis la barre de prix, la ligne de contexte et le bouton de commande
	// réglages de la planche (espacement, fond d'aperçu) dans une bulle de la barre d'outils, plus d'en-tête
	const [plancheSettingsOpen, setPlancheSettingsOpen] = useState(false);
	// téléphone : vue de travail courante ; la bulle de réglages se ferme quand on change de vue (revue Neo)
	const [mobileView, setMobileView] = useState<'logos' | 'planche' | 'recap'>('logos');
	useEffect(() => { setPlancheSettingsOpen(false); }, [mobileView]);
	// défilement vers une section APRÈS que React a affiché la bonne vue (revue Neo : sur téléphone la section pouvait être encore masquée)
	const [pendingScroll, setPendingScroll] = useState<'textile' | 'logos' | null>(null);
	useEffect(() => {
		if (!pendingScroll) return;
		const sel = pendingScroll === 'textile' ? '.dtf-textile' : '.dtf-items-title';
		const el = document.querySelector(sel);
		if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
		setPendingScroll(null);
	}, [pendingScroll, mobileView]);
	const scrollToTextile = () => {
		setColleFlash(true);
		setMobileView('logos');
		setPendingScroll('textile');
	};
	// la mascotte (position fixe) se cale au-dessus de la barre du bas, quelle que soit sa hauteur (mobile : colle + boutons)
	const pricebarRef = useRef<HTMLElement | null>(null);
	useEffect(() => {
		const el = pricebarRef.current;
		if (!el || typeof ResizeObserver === 'undefined') return undefined;
		const apply = () => document.documentElement.style.setProperty('--dtf-pricebar-h', `${el.offsetHeight}px`);
		apply();
		const ro = new ResizeObserver(apply);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);
	/** Congèle la planche côté serveur (bibliothèque, 15.10.2026) : chaque logo = fichier fini déposé à la boutique (version exacte), chaque texte = fichier,
	    puis composition du PDF d'impression par le serveur. Utilisé par « Préparer ma commande » ET par l'export admin (plus de PDF navigateur). */
	// Daniel 20.10 : « Préparer ma commande » aussi avec Paddy au milieu de l'écran (plus de petit avis en haut) — même carte que
	// l'enregistrement en bibliothèque ; le texte suit l'avancement (envoi de chaque logo, puis composition du fichier d'impression)
	const [orderCard, setOrderCard] = useState<{ title: string; text: string } | null>(null);
	// Daniel 21.10.2026 (checklist 9.11) : un manque au moment de commander (logo, textile…) s'affiche au MILIEU de l'écran avec Paddy qui
	// pointe du doigt et un bouton qui mène au manque, au lieu de l'avis discret en haut
	const [blocker, setBlocker] = useState<{ kind: 'logo' | 'refus' | 'analyse' | 'colle' } | null>(null);
	const blockerTimer = useRef<number | null>(null);
	const showBlocker = (kind: 'logo' | 'refus' | 'analyse' | 'colle') => {
		if (blockerTimer.current) window.clearTimeout(blockerTimer.current);
		setBlocker({ kind });
		blockerTimer.current = window.setTimeout(() => setBlocker(null), 9000);
	};
	const closeBlocker = () => { if (blockerTimer.current) { window.clearTimeout(blockerTimer.current); blockerTimer.current = null; } setBlocker(null); };
	const orderStep = (text: string) => setOrderCard(c => (c ? { ...c, text } : c));
	const freezeBoards = async (source: 'editeur-v2' | 'admin', glue: 'base' | 'anti') => {
		const byId = new Map(items.map(it => [it.id, it]));
		const blobOf = new Map<string, { finalBlobId: string; width: number | null; height: number | null }>();
		const needed = [...new Set(pack.planches.flatMap(p => p.placements.map(pl => pl.itemId)))];
		const versionOf = new Map<string, string | null>();
		for (const id of needed) {
			const pend = pendingSaveRef.current.get(id);
			if (pend) { if (source === 'editeur-v2') orderStep(i18next.t('dtf.commander-envoi-logo', { name: byId.get(id)?.name || '' }) as string); await pend; }
			const it = (pend ? itemsRef.current.find(x => x.id === id) : undefined) || byId.get(id);
			if (!it) throw new PlancheImageError(id);
			if (source === 'editeur-v2') orderStep(i18next.t('dtf.commander-envoi-logo', { name: it.name }) as string);
			else message.loading({ content: i18next.t('dtf.commander-envoi-logo', { name: it.name }) as string, key: 'lib-up', duration: 0 });
			if (it.library) {
				// 22.10 : élément repris par fichier AVANT le correctif (gardé dans ce navigateur) : sans version ni type → on les redemande au serveur
				if (!it.library.versionId && it.library.kind !== 'texte') {
					try {
						const pv = await logoPreview({ blob: it.library.blobId });
						if (pv.version_id || pv.item_kind === 'texte') {
							it.library = { ...it.library, versionId: pv.version_id, kind: pv.item_kind || null };
							changeItem(id, { library: it.library });
						}
					} catch { /* le serveur tranchera */ }
				}
				// 26.10 « Couleur unique » sur un logo de la bibliothèque : on imprime la copie recolorée par le serveur depuis le vrai fichier
				if (it.monoColor && it.library.versionId && it.library.kind !== 'texte') {
					const rc = await recolorLibraryLogo(it.library.versionId, it.monoColor);
					blobOf.set(id, { finalBlobId: rc.blob_id, width: rc.width, height: rc.height });
					versionOf.set(id, rc.version_id);
					continue;
				}
				blobOf.set(id, { finalBlobId: it.library.blobId, width: it.library.width, height: it.library.height });
				versionOf.set(id, it.library.versionId);
				continue;
			}
			if (it.text) {
				const b = await ensureItemBlobs(it);
				blobOf.set(id, b);
				versionOf.set(id, null);
				if (it.finalBlobId !== b.finalBlobId) changeItem(id, { finalBlobId: b.finalBlobId, finalBlobSha: b.finalSha, finalWidth: b.width, finalHeight: b.height });
				continue;
			}
			// un logo cite TOUJOURS sa version exacte : s'il n'a pas été gardé, il est enregistré ARCHIVÉ (invisible, conservé par la boutique)
			let saved = it.savedLogo;
			const fin = await ensureItemBlobs(it);
			if (!saved || it.finalBlobId !== fin.finalBlobId) {
				const r = await saveLogoToLibrary(it, it.savedLogo?.name || it.name, false);
				saved = { logoId: r.logo_id, versionId: r.version_id, name: r.name };
				changeItem(id, { finalBlobId: r.finalBlobId, finalBlobSha: r.finalSha, originalBlobId: r.originalBlobId || undefined, finalWidth: r.width, finalHeight: r.height, savedLogo: saved });
			}
			blobOf.set(id, { finalBlobId: fin.finalBlobId, width: fin.width, height: fin.height });
			versionOf.set(id, saved.versionId);
		}
		message.destroy('lib-up');
		const planches = pack.planches.map(p => ({
			width_cm: settings.laizeCm,
			// hauteur = celle que l'éditeur affiche et facture (pixels visibles) ; les boîtes des éléments TOURNÉS peuvent la dépasser par leurs coins
			// transparents : le serveur l'admet (un quart de la boîte) et coupe à la planche. Un élément NON tourné ne la dépasse que par arrondi (≤ 1 mm).
			// … et jamais plus que la planche maximale (20.10 : 100,1 cm par arrondi → « dimensions invalides » côté serveur)
			height_cm: Math.min(settings.maxPlancheCm, Math.ceil(Math.max(p.usedCm, ...p.placements.filter(pl => !pl.rotDeg).map(pl => pl.yCm + pl.hCm)) * 10 + 1e-6) / 10),
			qty: p.qty,
			glue,
			items: p.placements.map(pl => {
				const it = byId.get(pl.itemId)!;
				const rot = pl.rotDeg || 0;
				// boîte englobante arrondie au dixième de mm, recalée dans la laize si elle déborde d'une fraction de mm (arrondis du rangement)
				const bw = Math.round(pl.wCm * 100) / 10;
				const bh = Math.round(pl.hCm * 100) / 10;
				const bx = Math.max(0, Math.min(Math.round(pl.xCm * 100) / 10, Math.round(settings.laizeCm * 100) / 10 - bw));
				const by = Math.max(0, Math.round(pl.yCm * 100) / 10);
				return {
					blob_id: blobOf.get(it.id)!.finalBlobId,
					logo_version_id: versionOf.get(it.id) ?? null,
					// 22.10 : un texte repris d'une planche recommandée n'a pas de version de logo — on le déclare texte, sinon le serveur refuse
					item_kind: it.text || it.library?.kind === 'texte' ? ('texte' as const) : ('logo' as const),
					label: it.name,
					x_mm: bx,
					y_mm: by,
					width_mm: bw,
					height_mm: bh,
					item_width_mm: Math.round((rot ? it.widthCm : pl.wCm) * 100) / 10,
					item_height_mm: Math.round((rot ? it.heightCm : pl.hCm) * 100) / 10,
					rotation_deg: rot,
				};
			}),
		}));
		if (source === 'editeur-v2') orderStep(i18next.t('dtf.commander-compose') as string);
		return composeBoards(planches, source, getJobName());
	};

	// Daniel 22.10.2026 : « il n'y a aucun moment où ça propose de mettre un nom à la planche » → sans nom, Paddy le demande AVANT de figer
	// (le nom est celui de Ma bibliothèque › Mes planches). On peut continuer sans nom.
	const [askName, setAskName] = useState<string | null>(null);
	const onOrder = async (skip?: unknown) => {
		const skipName = skip === true;
		if (orderBusy || orderLockRef.current) return; // verrou synchrone : deux clics dans la même frame ne partent pas deux fois (revue Neo)
		// revue Neo 15.10 : le bouton mène au premier manque — d'abord un logo, puis le textile
		if (!pack.planches.length) {
			showBlocker(items.length === 0 ? 'logo' : pack.rejected.length ? 'refus' : 'analyse');
			setMobileView('logos');
			setPendingScroll('logos');
			return;
		}
		if (!colle) {
			// pas de colle choisie : Paddy le dit au milieu de l'écran, le bouton mène au sélecteur, on ne part pas
			showBlocker('colle');
			scrollToTextile();
			return;
		}
		// Bibliothèque (Daniel 09.10 / 15.10.2026) : plus de PDF fabriqué dans le navigateur. Les fichiers finis montent dans la boutique
		// (bucket privé), le serveur compose le fichier d'impression et FIGE la planche ; la commande cite cette révision.
		if (!isLoggedIn()) {
			message.warning(i18next.t('dtf.commander-connexion') as string, 8);
			window.location.href = `/login.html?next=${encodeURIComponent('editeur-v2/')}`;
			return;
		}
		if (!skipName && !getJobName()) {
			setAskName('');
			return;
		}
		setOrderBusy(true); orderLockRef.current = true;
		setOrderCard({ title: i18next.t('dtf.commander-envoi') as string, text: i18next.t('dtf.commander-envoi-aide') as string });
		try {
			const boards = await freezeBoards('editeur-v2', colle);
			const out = [];
			for (let i = 0; i < boards.length; i++) {
				const b = boards[i];
				let thumb: string | undefined;
				try {
					if (b.preview_url) thumb = await smallThumb(await fetchToDataUrl(b.preview_url));
				} catch {
					thumb = undefined;
				}
				// Daniel 22.10 (checklist 10.10) : la ligne de commande porte le NOM de la planche donné dans l'éditeur, pas un nom technique
				const jn = getJobName();
				// nom « fichier » de la planche = nom donné + planche N ; la page de commande y ajoute la quantité, le nom du CLIENT (profil) et un suffixe
				// anti-doublon au passage au panier, exactement comme pour un fichier importé à la main (Daniel 22.10 : « 1x-DTF-Suisse-paddy-test-planche-1-k3j9d2 »)
				const rowName = jn ? `${jn} planche ${i + 1}` : `Planche-${i + 1}-editeur-DTFSwiss_${settings.laizeCm}x${Math.round(b.height_mm / 10)}cm_${b.qty}x.pdf`;
				out.push({ name: rowName, board_version_id: b.board_version_id, thumb, wcm: settings.laizeCm, hcm: Math.round(b.height_mm) / 10, qty: b.qty, index: i, glue: colle });
			}
			await savePendingOrder({ createdAt: new Date().toISOString(), source: 'editeur-v2', planches: out, glue: colle });
			orderStep(i18next.t('dtf.commander-vers-panier') as string);
			window.location.href = ORDER_PAGE;
		} catch (e) {
			setOrderCard(null);
			if (e instanceof LibraryAuthError) {
				message.warning(i18next.t('dtf.commander-connexion') as string, 8);
				window.location.href = `/login.html?next=${encodeURIComponent('editeur-v2/')}`;
				return;
			}
			if (e instanceof PlancheImageError) message.error(i18next.t('dtf.commander-logo-illisible', { name: e.itemName }) as string, 8);
			else message.error(((e as Error)?.message ? `${i18next.t('dtf.commander-erreur')} ${(e as Error).message}` : i18next.t('dtf.commander-erreur')) as string, 8);
			setOrderBusy(false); orderLockRef.current = false;
		}
	};

	// ---- Bibliothèque (15.10.2026) : garder un logo fini, reprendre un logo, recommander une planche ----
	const [loggedIn, setLoggedIn] = useState<boolean>(() => isLoggedIn());
	useEffect(() => {
		const sync = () => setLoggedIn(isLoggedIn());
		window.addEventListener('storage', sync);
		window.addEventListener('focus', sync);
		return () => {
			window.removeEventListener('storage', sync);
			window.removeEventListener('focus', sync);
		};
	}, []);
	const [libOpen, setLibOpen] = useState(false);
	const [libCount, setLibCount] = useState<number | null>(null);
	const [libRefresh, setLibRefresh] = useState(0);
	const [savingIds, setSavingIds] = useState<string[]>([]);
	useEffect(() => {
		if (!loggedIn) {
			setLibCount(null);
			return;
		}
		listLogos()
			.then(l => setLibCount(l.length))
			.catch(() => setLibCount(null));
	}, [loggedIn, libRefresh]);
	// Daniel 20.10 : l'enregistrement en bibliothèque prend quelques secondes et le petit avis en haut passait inaperçu (« on s'agace ») →
	// Paddy au MILIEU de l'écran, au-dessus de tout : « J'enregistre ton logo… », puis « C'est gardé ! » avec « Voir », qui s'efface seul.
	const [libSave, setLibSave] = useState<{ phase: 'saving' | 'done'; name: string; keep: boolean } | null>(null);
	const libSaveTimer = useRef<number | null>(null);
	// 22.10 : enregistrement en arrière-plan par élément — « Préparer ma commande » l'attend au lieu de renvoyer le même fichier en parallèle
	// (deux envois de la même clé → « URL d'envoi indisponible : The resource already exists »)
	const pendingSaveRef = useRef<Map<string, Promise<void>>>(new Map());
	const saveItemToLibrary = async (id: string, meta: { keep: boolean; name: string }) => {
		const it = itemsRef.current.find(x => x.id === id);
		if (!it || savingIds.includes(id)) return;
		let saveDone: () => void = () => undefined;
		pendingSaveRef.current.set(id, new Promise<void>(res => { saveDone = res; }));
		setSavingIds(prev => [...prev, id]);
		if (libSaveTimer.current) { window.clearTimeout(libSaveTimer.current); libSaveTimer.current = null; }
		setLibSave({ phase: 'saving', name: meta.name, keep: meta.keep });
		try {
			const r = await saveLogoToLibrary(it, meta.name, meta.keep);
			setItems(prev => prev.map(x => (x.id === id ? { ...x, name: meta.keep ? r.name || meta.name : x.name, finalBlobId: r.finalBlobId, finalBlobSha: r.finalSha, originalBlobId: r.originalBlobId || undefined, finalWidth: r.width, finalHeight: r.height, savedLogo: { logoId: r.logo_id, versionId: r.version_id, name: r.name } } : x)));
			if (meta.keep) {
				setLibRefresh(v => v + 1);
				setLibSave({ phase: 'done', name: r.name || meta.name, keep: true });
				libSaveTimer.current = window.setTimeout(() => setLibSave(null), 4500);
			} else setLibSave(null);
		} catch (e) {
			setLibSave(null);
			if (e instanceof LibraryAuthError) message.warning(i18next.t('dtf.biblio-connexion') as string, 8);
			else message.error({ content: `${i18next.t('dtf.biblio-garde-echec')} ${(e as Error)?.message || ''}`, duration: 8 });
		} finally {
			pendingSaveRef.current.delete(id); saveDone();
			setSavingIds(prev => prev.filter(x => x !== id));
		}
	};
	/** « Ajouter à la planche » : l'aperçu (≤ 1200 px, signé 5 min) devient le src de l'élément ; le vrai fichier reste à la boutique. */
	const addFromLibrary = async (ref: { version?: string; blob?: string }, opts?: { widthCm?: number; qty?: number; name?: string }) => {
		const pv = await logoPreview(ref);
		if (!pv.preview_url) throw new Error(i18next.t('dtf.biblio-apercu-indispo') as string);
		const src = await fetchToDataUrl(pv.preview_url);
		const w = pv.width && pv.height ? pv.width / pv.height : pv.preview_width && pv.preview_height ? pv.preview_width / pv.preview_height : 1;
		const usableW = settings.laizeCm;
		const widthCm = round1(Math.min(opts?.widthCm || 10, usableW, settings.maxPlancheCm * w));
		const id = uuid();
		const name = opts?.name || pv.name || i18next.t('dtf.biblio-element') as string;
		setItems(prev => [
			...prev,
			{
				id,
				name: dedupName(name, prev.map(x => x.name)),
				src,
				kind: pv.vectorized ? 'svg' : 'raster',
				aspect: w,
				widthCm,
				heightCm: round1(widthCm / w),
				qty: Math.max(1, opts?.qty || 1),
				vectorized: pv.vectorized || undefined,
				library: { logoId: pv.logo_id, versionId: pv.version_id, blobId: pv.blob_id, name: pv.name, width: pv.width, height: pv.height, vector: pv.vectorized, kind: pv.item_kind || null },
				finalBlobId: pv.blob_id,
			},
		]);
		setCardOpenId(id);
		return id;
	};
	const onAddLogo = async (l: LibraryLogo) => {
		if (!l.version_id) throw new Error(i18next.t('dtf.biblio-apercu-indispo') as string);
		await addFromLibrary({ version: l.version_id });
	};
	/** « Recommander » : mêmes logos, mêmes tailles, quantités reprises ; l'éditeur range à nouveau et le prix est celui du jour. */
	const onReorder = async (b: LibraryBoardDetail) => {
		const groups = new Map<string, { ref: { version?: string; blob?: string }; w: number; n: number; name: string }>();
		for (const it of b.items) {
			const key = `${it.logo_version_id && it.logo_active ? 'v:' + it.logo_version_id : 'b:' + it.blob_id}|${it.item_width_mm}x${it.item_height_mm}`;
			const g = groups.get(key);
			if (g) g.n += 1;
			else groups.set(key, { ref: it.logo_version_id && it.logo_active ? { version: it.logo_version_id } : { blob: it.blob_id }, w: Number(it.item_width_mm) / 10, n: 1, name: it.logo_name || it.label || '' });
		}
		let added = 0;
		for (const g of groups.values()) {
			try {
				await addFromLibrary(g.ref, { widthCm: g.w, qty: g.n * Math.max(1, b.qty), name: g.name });
				added++;
			} catch (e) {
				message.warning(`${g.name || '?'} : ${(e as Error).message}`, 6);
			}
		}
		if (b.glue === 'anti' || b.glue === 'base') chooseColle(b.glue);
		if ((b.title || '').trim()) onJobName(b.title.trim()); // Daniel 22.10 : la planche recommandée reprend son nom
		message.success(i18next.t('dtf.biblio-recommande-ok', { n: added }) as string, 8);
	};
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'z') return;
			const t = e.target as HTMLElement | null;
			if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
			e.preventDefault();
			if (e.shiftKey) redo();
			else undo();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	// ---- Écran d'attente (index.html) : retiré seulement quand la planche sauvegardée est remise en place ET affichée ----
	// Daniel 20.10.2026 : « Paddy disparaît, puis pendant un moment tout est là mais rien ne s'affiche » (restauration des logos après le montage).
	const hideSplash = () => {
		const el = document.getElementById('dtf-splash');
		if (!el) return;
		el.classList.add('out');
		setTimeout(() => el.remove(), 400);
	};
	/** Attend que les images visibles de l'éditeur soient décodées (au plus maxMs), puis deux images peintes. */
	const whenPainted = (maxMs: number): Promise<void> =>
		new Promise(resolve => {
			const start = Date.now();
			const tick = () => {
				const imgs = Array.from(document.querySelectorAll<HTMLImageElement>('.dtf-editor img, .dtf-workspace img, .dtf-item-card img'));
				const pending = imgs.some(im => !im.complete);
				if (!pending || Date.now() - start > maxMs) {
					requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
					return;
				}
				setTimeout(tick, 80);
			};
			tick();
		});

	// ---- Sauvegarde auto locale : un refresh ne vide plus la planche ----
	const restored = useRef(false);
	useEffect(() => {
		let hadSaved = false;
		loadItems()
			.then(saved => {
				if (saved && saved.length) {
					hadSaved = true;
					skipHist.current = true;
					// l'écran d'attente le dit : on remet les logos en place
					try { const t = document.querySelector('#dtf-splash .t'); if (t) t.textContent = i18next.t('dtf.splash-restaure', { n: saved.length }) as string; } catch (_e) { /* ignore */ }
					setItems(prev => (prev.length ? prev : saved));
					message.info(i18next.t('dtf.planche-restauree'), 3);
				} else {
					// nouveau brouillon (revue Neo 15.10) : le textile de la commande précédente ne doit pas être repris en silence
					setColle(null);
					try { localStorage.removeItem('dtf_v2_colle'); } catch (_e) { /* ignore */ }
				}
			})
			.finally(() => {
				restored.current = true;
				// sans sauvegarde : l'éditeur est prêt tout de suite ; avec : on attend que les logos soient peints (au plus 8 s)
				whenPainted(hadSaved ? 8000 : 300).then(hideSplash);
			});
	}, []);
	// ---- Tout vider (Daniel 20.10.2026) : repartir d'un éditeur vide, sauvegarde locale comprise ----
	const clearAll = () => {
		Modal.confirm({
			title: i18next.t('dtf.tout-vider-titre') as string,
			content: i18next.t('dtf.tout-vider-texte') as string,
			okText: i18next.t('dtf.tout-vider-ok') as string,
			okButtonProps: { danger: true },
			cancelText: i18next.t('dtf.annuler') as string,
			onOk: () => {
				setItems([]);
				setColle(null);
				try { localStorage.removeItem('dtf_v2_colle'); } catch (_e) { /* ignore */ }
				void saveItems([]);
				message.success(i18next.t('dtf.tout-vider-fait'), 3);
			},
		});
	};
	useEffect(() => {
		if (!restored.current) return undefined;
		const t = setTimeout(() => saveItems(items), 800);
		return () => clearTimeout(t);
	}, [items]);
	// fermeture/rafraîchissement avant le débounce de 800 ms → on flushe la sauvegarde
	const itemsRef = useRef(items);
	itemsRef.current = items;
	useEffect(() => {
		const flush = () => {
			if (restored.current) saveItems(itemsRef.current);
		};
		window.addEventListener('pagehide', flush);
		return () => window.removeEventListener('pagehide', flush);
	}, []);

	// deux onglets ouverts = la sauvegarde locale de l'un écrase celle de l'autre → on prévient
	useEffect(() => {
		if (typeof BroadcastChannel === 'undefined') return undefined;
		const ch = new BroadcastChannel('dtf_v2_presence');
		let warned = false;
		const warn = () => {
			if (warned) return;
			warned = true;
			message.warning(i18next.t('dtf.autre-onglet'), 8);
		};
		ch.onmessage = e => {
			if (e.data === 'hello') {
				ch.postMessage('here');
				warn();
			} else if (e.data === 'here') warn();
		};
		ch.postMessage('hello');
		return () => ch.close();
	}, []);

	// ---- Assistante marmotte flottante (aide + tarifs), repliée par défaut ----
	const [assistOpen, setAssistOpen] = useState(false);

	// ---- Visite guidée (première visite, ou bouton ❓ Aide) ----
	// Test humain 15.10.2026 : pas de visite automatique sur téléphone (< 640 px : elle couvrait tout le panneau), elle reste
	// derrière « ❓ Aide ». Le pas courant est CONTRÔLÉ (revue Neo) : mise en pause pendant une fenêtre, reprise au même pas.
	const [tourOpen, setTourOpen] = useState(() => localStorage.getItem('dtf_editor_tour_done') !== '1' && window.innerWidth >= 640);
	const [tourStep, setTourStep] = useState(0);
	const closeTour = () => {
		localStorage.setItem('dtf_editor_tour_done', '1');
		setTourOpen(false);
		setTourStep(0);
	};
	const tourSteps: TourProps['steps'] = [
		{
			title: i18next.t('dtf.tour-import-t') as string,
			description: i18next.t('dtf.tour-import-d') as string,
			target: () => document.querySelector('.dtf-import-btn') as HTMLElement,
		},
		{
			title: i18next.t('dtf.tour-texte-t') as string,
			description: i18next.t('dtf.tour-texte-d') as string,
			target: () => document.querySelector('.dtf-text-btn') as HTMLElement,
		},
		{
			title: i18next.t('dtf.tour-planche-t') as string,
			description: i18next.t('dtf.tour-planche-d') as string,
			target: () => document.querySelector('.dtf-planche-board') as HTMLElement,
		},
		{
			title: i18next.t('dtf.tour-toolbar-t') as string,
			description: i18next.t('dtf.tour-toolbar-d') as string,
			target: () => document.querySelector('.dtf-ws-toolbar') as HTMLElement,
		},
		{
			title: i18next.t('dtf.tour-prix-t') as string,
			description: i18next.t('dtf.tour-prix-d') as string,
			target: () => document.querySelector('.dtf-pricebar') as HTMLElement,
		},
	];

	const changeBoardBg = (v: string | number) => {
		const dark = v === 'sombre';
		setBoardDark(dark);
		localStorage.setItem('dtf_v2_board_dark', dark ? '1' : '0');
	};

	const margeMm = Math.min(15, Math.max(5, settings.margeMm));
	const changeMarge = (v: number) => {
		const s = { ...settings, margeMm: Math.min(15, Math.max(5, Math.round(v))) };
		setSettings(s);
		saveSettings(s);
	};

	// Nesting async (décodage des masques alpha) + débouncé (frappe dans les champs cm/qté)
	const [pack, setPack] = useState<PackResult>({ planches: [], totalCm: 0, rejected: [] });
	useEffect(() => {
		let alive = true;
		const t = setTimeout(() => {
			nestItems(items, settings)
				.then(res => {
					if (alive) setPack(res);
				})
				.catch((): void => undefined);
		}, 120);
		return () => {
			alive = false;
			clearTimeout(t);
		};
	}, [items, settings]);
	const price = useMemo(() => computePrice(pack.totalCm, settings, colle), [pack.totalCm, settings, colle]);
	// tant que le textile n'est pas choisi, on montre AUSSI le montant anti-sublimation (jamais un prix nu — revue Neo 15.10)
	const priceAnti = useMemo(() => computePrice(pack.totalCm, settings, 'anti'), [pack.totalCm, settings]);
	// raison exacte pour laquelle il n'y a pas de planche (revue Neo : « pas de planche » ≠ « pas de logo »)
	const orderReasonKey =
		items.length === 0 ? 'dtf.commander-raison-logo'
		: pack.rejected.length ? 'dtf.commander-raison-refus'
		: 'dtf.commander-raison-analyse';
	const orderLockRef = useRef(false);

	// Alertes qualité à l'échelle choisie (traits < 0,5 mm, DPI insuffisants) :
	// recalcul débouncé quand un logo ou ses cotes changent
	const [thinIds, setThinIds] = useState<string[]>([]);
	const [lowDpi, setLowDpi] = useState<Record<string, number>>({});
	const [inspectId, setInspectId] = useState<string | null>(null);
	const [bgEditId, setBgEditId] = useState<string | null>(null);
	// plusieurs fichiers importés d'un coup → les assistants s'enchaînent un par un
	// Téléchargement des PDF : admins du site uniquement (Daniel 07.10.2026) ; localhost + ?telecharger=1 pour les tests
	const [canDownload, setCanDownload] = useState<boolean>(() => isDownloadUnlocked());
	useEffect(() => {
		let alive = true;
		fetchAdminDownload().then(ok => { if (alive && ok) setCanDownload(true); });
		const again = () => { fetchAdminDownload().then(ok => { if (alive && ok) setCanDownload(true); }); };
		window.addEventListener('focus', again);
		const iv = setInterval(again, 10 * 60 * 1000);
		return () => { alive = false; window.removeEventListener('focus', again); clearInterval(iv); };
	}, []);
	// 16.10 : une nouvelle version de l'éditeur installée pendant qu'on travaille → avis discret + bouton, jamais de rechargement
	// forcé au milieu d'une planche (le rechargement automatique n'a lieu qu'au chargement de la page, voir serviceWorker.ts)
	// 20.10 (Daniel, resté 3 fois sur l'ancienne version sans le voir : « comment je fais pour mettre à jour l'éditeur ») : l'avis
	// antd était caché derrière la fenêtre de l'assistant → bandeau fixe au-dessus de tout (z-index > modales), et rechargement
	// automatique au RETOUR sur l'onglet (visibilitychange → visible) : on ne coupe jamais quelqu'un en plein travail, mais on
	// n'attend pas non plus qu'il trouve le bouton. La planche est conservée (IndexedDB).
	const [newVersion, setNewVersion] = useState(false);
	useEffect(() => {
		const onNew = () => setNewVersion(true);
		window.addEventListener('dtf:new-version', onNew);
		return () => window.removeEventListener('dtf:new-version', onNew);
	}, []);
	useEffect(() => {
		if (!newVersion) return undefined;
		const onVisible = () => { if (document.visibilityState === 'visible') window.location.reload(); };
		document.addEventListener('visibilitychange', onVisible);
		return () => document.removeEventListener('visibilitychange', onVisible);
	}, [newVersion]);
	const [wizardQueue, setWizardQueue] = useState<string[]>([]);
	const wizardId = wizardQueue.length ? wizardQueue[0] : null;
	// garde-fou (09.10) : un id en tête de file qui n'existe plus (logo supprimé entre-temps) bloquerait la file pour toujours —
	// PrepWizard rend null sans jamais appeler onClose → plus aucun assistant à l'import suivant
	useEffect(() => {
		if (wizardId && !items.some(it => it.id === wizardId)) setWizardQueue(q => q.filter(id => id !== wizardId));
	}, [wizardId, items]);
	useEffect(() => {
		let alive = true;
		const t = setTimeout(async () => {
			const ids: string[] = [];
			const dpiMap: Record<string, number> = {};
			for (const it of items) {
				try {
					// logo repris de la bibliothèque : déjà préparé ; src = aperçu ≤ 1200 px → les dpi se calculent sur le VRAI fichier
					if (it.library) {
						if (!it.library.vector && it.library.width) {
							const dpi = it.library.width / (it.widthCm / 2.54);
							if (dpi < MIN_DPI) dpiMap[it.id] = Math.round(dpi);
						}
						continue;
					}
					if (await hasThinStrokes(it.src, it.widthCm, it.heightCm)) ids.push(it.id);
					if (it.kind !== 'svg') {
						const dpi = await effectiveDpi(it.src, it.widthCm);
						if (dpi < MIN_DPI) dpiMap[it.id] = Math.round(dpi);
					}
				} catch {
					// image illisible : pas d'alerte
				}
			}
			if (!alive) return;
			setThinIds(prev => (prev.join('|') === ids.join('|') ? prev : ids));
			setLowDpi(prev => (JSON.stringify(prev) === JSON.stringify(dpiMap) ? prev : dpiMap));
		}, 300);
		return () => {
			alive = false;
			clearTimeout(t);
		};
	}, [items]);

	// Fiche qualité par logo (plage de largeurs imprimables) — dépend du fichier, pas des cotes
	const [quality, setQuality] = useState<Record<string, QualityInfo>>({});
	useEffect(() => {
		let alive = true;
		(async () => {
			for (const it of items) {
				const cur = quality[it.id];
				if (cur && cur.src === it.src) continue;
				try {
					if (it.library) {
						// fiche synthétique : dimensions réelles du fichier de la boutique, pas celles de l'aperçu
						const pxW = it.library.width || 0;
						const pxH = it.library.height || 0;
						const info: QualityInfo = { src: it.src, pxW, pxH, minCm: null, maxCm: it.library.vector || !pxW ? null : Math.floor((pxW / MIN_DPI) * 2.54 * 10) / 10 };
						if (!alive) return;
						setQuality(prev => ({ ...prev, [it.id]: info }));
						continue;
					}
					const info = await analyzeQuality(it.src, it.kind, it.aspect, settings.laizeCm, MIN_DPI, it.widthCm);
					if (!alive) return;
					setQuality(prev => ({ ...prev, [it.id]: info }));
				} catch {
					// image illisible : pas de fiche
				}
			}
		})();
		return () => {
			alive = false;
		};
	}, [items, quality, settings.laizeCm]);

	const handleFiles = async (files: FileList | null) => {
		if (!files || !files.length) return;
		const list = Array.from(files);
		const hide = message.loading(i18next.t('dtf.import-en-cours', { n: list.length }), 0);
		try {
			for (const file of list) {
				try {
					const imp = await importDTFFile(file, settings.maxFichierMb);
					const aspect = imp.aspect || 1;
					const usableW = settings.laizeCm;
					const widthCm = round1(Math.min(10, usableW));
					const id = uuid();
					// à CHAQUE import, l'assistant « Préparer le logo » s'ouvre : UNE seule
					// fenêtre, étapes Taille → Couleurs (pipette) → Qualité (DPI/plage/traits)
					if (localStorage.getItem('dtf_v2_no_autoinspect') !== '1') setWizardQueue(prev => [...prev, id]);
					// Daniel 09.10 (« l'éditeur du logo ne s'ouvre plus ») : depuis les cartes repliées, le logo importé doit arriver
					// carte OUVERTE, outils visibles (sans le sélectionner sur la planche)
					setCardOpenId(id);
					// étape 1 (Daniel 09.10, revue Neo) : mesurer le logo à l'import et journaliser, SANS rien décider
					if (imp.kind !== 'svg') {
						computeLogoMetrics(imp.src)
							.then(m => {
								const w = window as unknown as { __dtfLogoMetrics?: Record<string, LogoMetrics> };
								(w.__dtfLogoMetrics = w.__dtfLogoMetrics || {})[id] = m;
								setLogoMetrics(prev => ({ ...prev, [id]: m }));
								logLogoMetrics(m, { kind: imp.kind, widthCm });
							})
							.catch(() => undefined);
					}
					setItems(prev => [
						...prev,
						{
							id,
							name: dedupName(imp.name, prev.map(x => x.name)),
							src: imp.src,
							kind: imp.kind,
							svgText: imp.svgText,
							originalDataUrl: imp.originalDataUrl,
							// bibliothèque : le fichier importé TEL QUEL (jamais modifié ensuite) → version « original » côté boutique
							importOriginal: imp.originalDataUrl || (imp.svgText ? `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(imp.svgText)))}` : undefined),
							aspect,
							widthCm,
							heightCm: round1(widthCm / aspect),
							qty: 1,
						},
					]);
				} catch (e) {
					const msg =
						e instanceof ImportError ? e.message : (i18next.t('dtf.err-import-generique', { nom: file.name }) as string);
					message.error(msg, 6);
				}
			}
		} finally {
			hide();
		}
	};

	const addTextEntries = (entries: TextEntry[], heightsCm: number[]) => {
		const usableW = settings.laizeCm;
		// Une série (groupId) PAR taille : chaque taille reste réglable/supprimable en bloc
		setItems(prev => [
			...prev,
			...heightsCm.flatMap(heightCm => {
				const groupId = entries.length > 1 ? uuid() : undefined;
				return entries.map(en => {
					let h = heightCm;
					let w = h * en.built.aspect;
					if (w > usableW) {
						w = usableW;
						h = w / en.built.aspect;
					}
					return {
						id: uuid(),
						name: en.value,
						src: en.built.src,
						kind: 'svg' as const,
						svgText: en.built.svgText,
						aspect: en.built.aspect,
						widthCm: round1(w),
						heightCm: round1(h),
						qty: 1,
						text: en.style,
						groupId,
					};
				});
			}),
		]);
		setTextOpen(false);
	};

	const saveTextEdit = (id: string, entry: TextEntry) => {
		const usableW = settings.laizeCm;
		setItems(prev =>
			prev.map(it => {
				if (it.id !== id) return it;
				if (it.free) {
					// déformation volontaire : on garde les dimensions telles quelles
					return {
						...it,
						name: entry.value,
						src: entry.built.src,
						svgText: entry.built.svgText,
						aspect: entry.built.aspect,
						text: entry.style,
					};
				}
				let h = it.heightCm;
				let w = h * entry.built.aspect;
				if (w > usableW) {
					w = usableW;
					h = w / entry.built.aspect;
				}
				return {
					...it,
					name: entry.value,
					src: entry.built.src,
					svgText: entry.built.svgText,
					aspect: entry.built.aspect,
					widthCm: round1(w),
					heightCm: round1(h),
					text: entry.style,
				};
			}),
		);
		setTextOpen(false);
		setTextEditId(null);
	};

	// #221 : couleur unique (null = retour aux couleurs d'origine).
	// SVG : réécriture vectorielle ; PDF/raster : teinte canvas de la silhouette (alpha conservé).
	const recolorItem = async (id: string, color: string | null) => {
		const target = items.find(it => it.id === id);
		if (!target) return;
		if (target.kind === 'svg' && target.svgText) {
			setItems(prev =>
				prev.map(it => {
					if (it.id !== id || !it.svgText) return it;
					const orig = it.origSvgText || it.svgText;
					if (!color)
						return { ...it, svgText: orig, src: svgToDataUrl(orig), origSvgText: undefined, monoColor: undefined };
					const rec = recolorSvg(orig, color);
					return { ...it, svgText: rec, src: svgToDataUrl(rec), origSvgText: orig, monoColor: color };
				}),
			);
			return;
		}
		const orig = target.origSrc || target.src;
		const tinted = color ? await recolorRaster(orig, color) : null;
		setItems(prev =>
			prev.map(it => {
				if (it.id !== id) return it;
				if (!color) return { ...it, src: it.origSrc || it.src, origSrc: undefined, monoColor: undefined };
				return { ...it, src: tinted!, origSrc: orig, monoColor: color };
			}),
		);
	};

	const [upscaling, setUpscaling] = useState<string[]>([]);
	const upscaleItem = async (id: string) => {
		const it = items.find(x => x.id === id);
		if (!it || upscaling.includes(id)) return;
		setUpscaling(prev => [...prev, id]);
		const hide = message.loading(i18next.t('dtf.amelioration-en-cours'), 0);
		try {
			const up = await upscaleImage(it.src);
			// 20.10.2026 : l'image a changé pendant le calcul (fond, épaississement…) → on ne l'écrase pas avec l'amélioration de l'ancienne
			const cur = itemsRef.current.find(x => x.id === id);
			if (!cur) return;
			if (cur.src !== it.src) {
				message.info(i18next.t('dtf.amelioration-remplacee'), 8);
				return;
			}
			setItems(prev =>
				prev.map(x =>
					x.id === id ? { ...x, src: up.src, originalDataUrl: up.src, aspect: up.aspect, improved: true } : x,
				),
			);
			message.success(i18next.t('dtf.amelioration-ok'), 4);
		} catch (e) {
			if (e instanceof UpscaleMaxedError) message.info(i18next.t('dtf.amelioration-max'), 8);
			else message.error(i18next.t('dtf.amelioration-echec'), 6);
		} finally {
			hide();
			setUpscaling(prev => prev.filter(x => x !== id));
		}
	};

	// Lissage des bords sans vectoriser (Daniel 15.10) : 100 % navigateur, sur l'image courante (fond déjà retiré conservé)
	const [smoothing, setSmoothing] = useState<string[]>([]);
	const smoothItem = async (id: string): Promise<boolean> => {
		const it = itemsRef.current.find(x => x.id === id);
		if (!it || it.kind === 'svg' || smoothing.includes(id)) return false;
		setSmoothing(prev => [...prev, id]);
		const hide = message.loading(i18next.t('dtf.lisser-en-cours'), 0);
		try {
			const out = await smoothEdges(it.src);
			if (!out) {
				message.info(i18next.t('dtf.lisser-rien'), 6);
				return false;
			}
			setItems(prev => prev.map(x => (x.id === id ? { ...x, src: out.src, originalDataUrl: out.src, kind: 'raster', svgText: undefined, aspect: out.aspect, heightCm: x.free ? x.heightCm : round1(x.widthCm / out.aspect), smoothed: true, finalBlobId: undefined } : x)));
			message.success(i18next.t('dtf.lisser-ok'), 4);
			return true;
		} catch {
			message.error(i18next.t('dtf.lisser-echec'), 6);
			return false;
		} finally {
			hide();
			setSmoothing(prev => prev.filter(x => x !== id));
		}
	};

	// mesures d'analyse (étape 1) : nourrissent la recommandation « vectoriser ? »
	const [logoMetrics, setLogoMetrics] = useState<Record<string, LogoMetrics>>({});
	const [vectorizing, setVectorizing] = useState<string[]>([]);
	const vectorizeItem = async (id: string) => {
		const it = items.find(x => x.id === id);
		if (!it || it.kind === 'svg' || vectorizing.includes(id)) return;
		setVectorizing(prev => [...prev, id]);
		const hide = message.loading(i18next.t('dtf.vecto-en-cours'), 0);
		try {
			const { svg, remaining } = await vectorizeImage(it.src);
			const file = new File([svg], `${it.name.replace(/\.[a-z0-9]+$/i, '')}.svg`, { type: 'image/svg+xml' });
			const imp = await importDTFFileForVecto(file, settings.maxFichierMb);
			setItems(prev => prev.map(x => (x.id === id ? { ...x, src: imp.src, kind: 'svg', svgText: imp.svgText, originalDataUrl: undefined, aspect: imp.aspect || x.aspect, heightCm: round1(x.widthCm / (imp.aspect || x.aspect)), vectorized: true } : x)));
			message.success(i18next.t('dtf.vecto-ok', { n: remaining }), 5);
		} catch (e) {
			if (e instanceof VectorizeAuthError) message.warning(i18next.t('dtf.vecto-connexion'), 8);
			else if (e instanceof VectorizeQuotaError) message.info(String((e as Error).message || i18next.t('dtf.vecto-plafond')), 8);
			else message.error(i18next.t('dtf.vecto-echec'), 6);
		} finally {
			hide();
			setVectorizing(prev => prev.filter(x => x !== id));
		}
	};

	const [fixingThin, setFixingThin] = useState<string[]>([]);
	/** Retourne true si le logo a été modifié (false : aucun trait fin à cette taille, ou échec). */
	const fixThinItem = async (id: string, mode: ThinFixMode, garmentColor?: string): Promise<boolean> => {
		const it = items.find(x => x.id === id);
		if (!it || fixingThin.includes(id)) return false;
		let changed = false;
		setFixingThin(prev => [...prev, id]);
		const hide = message.loading(i18next.t('dtf.correction-en-cours'), 0);
		try {
			// plage impossible (traits fins sous min cm, DPI plafonnés à max cm) : on épaissit
			// pour la largeur que les DPI autorisent, pas pour la largeur affichée — sinon,
			// au-dessus de min cm, aucun trait n'est fin et la correction ne fait rien
			const q = quality[id] && quality[id].src === it.src ? quality[id] : undefined;
			let wCm = it.widthCm;
			let hCm = it.heightCm;
			if (q && q.maxCm !== null && q.minCm !== null && q.minCm > q.maxCm && wCm > q.maxCm) {
				hCm = Math.round(((it.heightCm * q.maxCm) / it.widthCm) * 10) / 10;
				wCm = q.maxCm;
			}
			const out = await fixThinStrokes(it.src, wCm, hCm, mode, garmentColor);
			if (out) {
				// le résultat est un raster : les SVG/PDF corrigés perdent leur source vectorielle
				setItems(prev =>
					prev.map(x =>
						x.id === id ? { ...x, src: out.src, originalDataUrl: out.src, kind: 'raster', svgText: undefined, thinFixed: true, finalBlobId: undefined } : x,
					),
				);
				message.success(i18next.t('dtf.correction-ok'), 4);
				changed = true;
			} else {
				// 20.10.2026 : la correction voit les mêmes traits que la détection ; « rien » = vraiment rien à cette taille
				message.info(i18next.t('dtf.correction-rien', { w: fmtCm(wCm) }), 6);
			}
		} catch {
			message.error(i18next.t('dtf.correction-echec'), 6);
		} finally {
			hide();
			setFixingThin(prev => prev.filter(x => x !== id));
		}
		return changed;
	};

	const applyBgResult = (id: string, out: { src: string; aspect: number }) => {
		setItems(prev =>
			prev.map(x =>
				x.id === id
					? {
							...x,
							src: out.src,
							originalDataUrl: out.src,
							kind: 'raster',
							svgText: undefined,
							aspect: out.aspect,
							heightCm: x.free ? x.heightCm : round1(x.widthCm / out.aspect),
							bgRemoved: true,
							finalBlobId: undefined,
					  }
					: x,
			),
		);
	};

	const changeItem = (id: string, patch: Partial<DTFItem>) =>
		setItems(prev => prev.map(it => (it.id === id ? { ...it, ...patch } : it)));
	const removeItem = (id: string) => {
		setItems(prev => prev.filter(it => it.id !== id));
		setWizardQueue(q => q.filter(x => x !== id)); // un logo supprimé n'a plus d'assistant en attente
	};

	// même visuel en plusieurs tailles (dos 25 cm + cœur 8 cm…) sans ré-importer
	const duplicateItem = (id: string) => {
		setItems(prev => {
			const i = prev.findIndex(it => it.id === id);
			if (i === -1) return prev;
			const src = prev[i];
			const copy: DTFItem = {
				...src,
				id: uuid(),
				name: copyName(src.name.replace(/ \(copie(?: \d+)?\)$/, ''), prev.map(x => x.name)),
				groupId: undefined,
			};
			return [...prev.slice(0, i + 1), copy, ...prev.slice(i + 1)];
		});
	};

	const totalPlanchesPhysiques = pack.planches.reduce((acc, p) => acc + p.qty, 0);

	// ---- Effet « wow » : étincelles à l'arrivée d'un logo + glow pendant un drag de fichier ----
	const [burstKey, setBurstKey] = useState(0);
	const prevItemCount = useRef(0);
	useEffect(() => {
		if (items.length > prevItemCount.current && !noAnim()) setBurstKey(k => k + 1);
		prevItemCount.current = items.length;
	}, [items.length]);
	useEffect(() => {
		if (!burstKey) return undefined;
		const t = setTimeout(() => setBurstKey(0), 1000);
		return () => clearTimeout(t);
	}, [burstKey]);

	const [dragOver, setDragOver] = useState(false);
	const dragDepth = useRef(0);
	const dragHasFiles = (e: React.DragEvent) => Array.from(e.dataTransfer.types || []).includes('Files');

	return (
		<div
			className={`dtf-editor dtf-mv-${mobileView}`}
			data-tenant={settings.tenantId}
			onDragEnter={e => {
				if (!dragHasFiles(e)) return;
				e.preventDefault();
				dragDepth.current++;
				setDragOver(true);
			}}
			onDragOver={e => {
				if (dragHasFiles(e)) e.preventDefault();
			}}
			onDragLeave={() => {
				dragDepth.current = Math.max(0, dragDepth.current - 1);
				if (!dragDepth.current) setDragOver(false);
			}}
			onDrop={e => {
				if (!dragHasFiles(e)) return;
				e.preventDefault();
				dragDepth.current = 0;
				setDragOver(false);
				handleFiles(e.dataTransfer.files);
			}}
		>
			<input
				ref={fileRef}
				type="file"
				accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,.svg,application/pdf,.pdf,.ai"
				multiple
				style={{ display: 'none' }}
				onChange={e => {
					handleFiles(e.target.files);
					e.target.value = '';
				}}
			/>
			<SiteTopbar />
			{/* Maquette v3.3 (Daniel + Neo 15.10.2026) : plus de deuxième en-tête ni de deuxième logo. Une ligne de contexte légère :
			    titre, nom de la commande (toujours visible, quel que soit le nombre de logos) et les sections à compléter. */}
			<div className="dtf-context" role="region" aria-label={i18next.t('dtf.titre-planche') as string}>
				<h1 className="dtf-context-title">{i18next.t('dtf.titre-planche')}</h1>
				<label className="dtf-jobname">
					<span className="dtf-jobname-label">
						{i18next.t('dtf.nom-commande')} <small>({i18next.t('dtf.optionnel')})</small>
					</span>
					<Input
						size="small"
						className="dtf-job-name"
						value={jobName}
						maxLength={40}
						placeholder={i18next.t('dtf.nom-commande-ex') as string}
						onChange={e => onJobName(e.target.value)}
					/>
					<span className="dtf-jobname-help" title={i18next.t('dtf.nom-commande-aide') as string} aria-label={i18next.t('dtf.nom-commande-aide') as string}>?</span>
				</label>
				<nav className="dtf-sections" aria-label={i18next.t('dtf.sections') as string}>
					<button type="button" className={`dtf-section-chip${colle ? ' is-done' : ' is-todo'}`} onClick={() => scrollToTextile()}>
						1 {i18next.t('dtf.section-textile')} {colle ? '✓' : `⚠ ${i18next.t('dtf.colle-a-choisir')}`}
					</button>
					<button type="button" className={`dtf-section-chip${items.length ? ' is-done' : ''}`} onClick={() => document.querySelector('.dtf-items-title')?.scrollIntoView({ block: 'start', behavior: 'smooth' })}>
						2 {i18next.t('dtf.section-logos')} {items.length ? `· ${items.length}` : ''}
					</button>
					<button type="button" className="dtf-section-chip dtf-section-order" onClick={() => document.querySelector('.dtf-order-btn-bottom')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })}>
						{i18next.t('dtf.section-commander')} ↘
					</button>
				</nav>
			</div>
			<div className="dtf-body">
				<ItemsPanel
					items={items}
					settings={settings}
					colle={colle}
					colleFlash={colleFlash}
					onChooseColle={chooseColle}
					rejected={pack.rejected}
					thin={thinIds}
					lowDpi={lowDpi}
					minDpi={MIN_DPI}
					quality={quality}
					onPrepare={id => setWizardQueue(q => (q.includes(id) ? q : [...q, id]))}
					onInspect={setInspectId}
					upscaling={upscaling}
					onUpscale={upscaleItem}
					onSmooth={smoothItem}
					smoothing={smoothing}
					fixingThin={fixingThin}
					onFixThin={fixThinItem}
					onBgEdit={setBgEditId}
					onChange={changeItem}
					onRecolor={recolorItem}
					onRemove={removeItem}
					onDuplicate={duplicateItem}
					selectedId={selectedId}
					onSelect={selectFromCard}
					onHoverItem={setHoverId}
					openCardId={cardOpenId}
					onVectorize={vectorizeItem}
					vectorizing={vectorizing}
					onImportClick={() => fileRef.current?.click()}
					canLibrary={loggedIn}
					libraryCount={libCount}
					onLibraryClick={() => {
						if (!loggedIn) {
							window.location.href = `/login.html?next=${encodeURIComponent('editeur-v2/')}`;
							return;
						}
						setLibOpen(true);
					}}
					onSaveToLibrary={id => {
						const it = items.find(x => x.id === id);
						if (it) void saveItemToLibrary(id, { keep: true, name: it.savedLogo?.name || it.name });
					}}
					savingIds={savingIds}
					onTextClick={() => {
						setTextEditId(null);
						setTextOpen(true);
					}}
					onEditText={id => {
						setTextEditId(id);
						setTextOpen(true);
					}}
				/>
				<main
					ref={wsRef}
					className={`dtf-workspace${panning ? ' dtf-panning' : ''}`}
					onPointerDown={onWsPointerDown}
					onPointerMove={onWsPointerMove}
					onPointerUp={onWsPointerEnd}
					onPointerCancel={onWsPointerEnd}
				>
					<div className="dtf-ws-toolbar">
						<div className="dtf-zoom-group" title={i18next.t('dtf.zoom-titre') as string}>
							<Button size="small" disabled={zoom <= 0.4} onClick={() => zoomBy(1 / 1.25)}>
								−
							</Button>
							<button type="button" className="dtf-zoom-val" onClick={() => setZoom(1)} title={i18next.t('dtf.zoom-reset') as string}>
								{Math.round(zoom * 100)} %
							</button>
							<Button size="small" disabled={zoom >= 3} onClick={() => zoomBy(1.25)}>
								+
							</Button>
						</div>
						<div className="dtf-history-group" role="group" aria-label={i18next.t('dtf.undo-titre') as string}>
							<Button size="small" className="dtf-undo-btn dtf-undo-main" title={i18next.t('dtf.undo-titre') as string} aria-label={i18next.t('dtf.undo-titre') as string} onClick={undo}>
								↩
							</Button>
							<Button size="small" className="dtf-redo-btn" title={i18next.t('dtf.redo-titre') as string} aria-label={i18next.t('dtf.redo-titre') as string} onClick={redo}>
								↪
							</Button>
						</div>
						<Button
							size="small"
							type={measureMode ? 'primary' : 'default'}
							className="dtf-measure-btn"
							title={i18next.t('dtf.mesurer-titre') as string}
							onClick={() => setMeasureMode(m => !m)}
						>
							{i18next.t('dtf.mesurer')}
						</Button>
						{/* Réglages de la planche : l'espacement (qui change le prix) reste lisible sans ouvrir le menu (revue Neo) */}
						<Popover
							trigger="click"
							placement="bottomLeft"
							open={plancheSettingsOpen}
							onOpenChange={setPlancheSettingsOpen}
							overlayClassName="dtf-planche-settings"
							content={
								<div className="dtf-planche-settings-body">
									<div className="dtf-marge-control" title={i18next.t('dtf.espacement-titre') as string}>
										<span className="dtf-marge-label">{i18next.t('dtf.espacement')}</span>
										<Slider min={5} max={15} step={1} value={margeMm} onChange={changeMarge} className="dtf-marge-slider" />
										<span className="dtf-marge-val">{margeMm} mm</span>
									</div>
									<Button size="small" className="dtf-settings-btn" onClick={() => { setPlancheSettingsOpen(false); setSettingsOpen(true); }}>
										{i18next.t('dtf.reglages-avances')}
									</Button>
								</div>
							}
						>
							<Button size="small" className="dtf-planche-settings-btn" title={i18next.t('dtf.reglages-titre') as string} aria-expanded={plancheSettingsOpen}>
								{i18next.t('dtf.reglages')} · {margeMm} mm
							</Button>
						</Popover>
						{/* Fond de l'aperçu (damier clair / sombre) : bouton VISIBLE dans la barre, plus caché dans « Réglages » où on le prenait
						    pour un réglage d'espacement (Daniel 19.10.2026). Mêmes boutons-échantillons que l'assistant : le bouton montre le fond. */}
						<div className="dtf-board-bg-toggle dtf-ws-bg-toggle" role="group" title={i18next.t('dtf.fond-titre') as string} aria-label={i18next.t('dtf.fond-titre') as string}>
							<span className="dtf-bg-toggle-label">{i18next.t('dtf.fond-barre')}</span>
							<button
								type="button"
								className={`dtf-bg-swatch dtf-bg-swatch-clair${!boardDark ? ' dtf-bg-swatch-on' : ''}`}
								aria-pressed={!boardDark}
								onClick={() => changeBoardBg('clair')}
							>
								{i18next.t('dtf.fond-clair-court')}
							</button>
							<button
								type="button"
								className={`dtf-bg-swatch dtf-bg-swatch-sombre${boardDark ? ' dtf-bg-swatch-on' : ''}`}
								aria-pressed={boardDark}
								onClick={() => changeBoardBg('sombre')}
							>
								{i18next.t('dtf.fond-sombre-court')}
							</button>
						</div>
						<Button
							size="small"
							className="dtf-help-btn"
							title={i18next.t('dtf.aide-revoir') as string}
							onClick={() => { setTourStep(0); setTourOpen(true); }}
						>
							{i18next.t('dtf.aide')}
						</Button>
						<Button
							size="small"
							danger
							className="dtf-help-btn dtf-clear-btn"
							title={i18next.t('dtf.tout-vider-titre') as string}
							disabled={items.length === 0}
							onClick={clearAll}
						>
							{i18next.t('dtf.tout-vider')}
						</Button>
						{canDownload && (
							<Button
								size="small"
								className="dtf-export-all-btn dtf-admin-only"
								disabled={pack.planches.length === 0}
								loading={zipBusy}
								title={i18next.t('dtf.export-tout-titre', { defaultValue: 'Télécharger toutes les planches (un PDF par planche, dans un ZIP)' }) as string}
								onClick={onExportAllPdf}
							>
								⬇ PDF (admin)
							</Button>
						)}
					</div>
					{pack.planches.length === 0 && (
						<>
							{/* accueil pro : l'éditeur d'abord — l'invite de dépôt vit DANS la planche vide,
							   les explications (prix, aide) sont dans l'assistante flottante en bas à droite */}
							<PlancheView
								canDownload={canDownload}
								onPdfServer={FEATURE_DOWNLOAD_TEST ? undefined : downloadPlancheViaServer}
								planche={{ placements: [], usedCm: settings.minPlancheCm, qty: 1 }}
								index={0}
								items={items}
								settings={settings}
								pxPerCm={PX_PER_CM * zoom}
								dark={boardDark}
								measure={measureMode}
								selectedId={selectedId}
								hoverId={hoverId}
								onSelect={selectFromBoard}
								invite={
									<button
										type="button"
										className="dtf-planche-invite"
										onClick={e => {
											e.stopPropagation();
											fileRef.current?.click();
										}}
									>
										<svg className="dtf-invite-icon" viewBox="0 0 24 24" aria-hidden="true">
											<path
												d="M12 16V4m0 0-4.2 4.2M12 4l4.2 4.2M4 15.5v2.8A1.7 1.7 0 0 0 5.7 20h12.6a1.7 1.7 0 0 0 1.7-1.7v-2.8"
												fill="none"
												stroke="currentColor"
												strokeWidth="1.8"
												strokeLinecap="round"
												strokeLinejoin="round"
											/>
										</svg>
										<span className="dtf-invite-title">{i18next.t('dtf.invite-t')}</span>
										<span className="dtf-invite-sub">{i18next.t('dtf.invite-s')}</span>
									</button>
								}
							/>
							<button type="button" className="dtf-price-hint" onClick={() => setAssistOpen(true)}>
								{i18next.t('dtf.prix-hint', { base: fmtMoney(settings.minCommande, settings.devise) })}
								<span className="dtf-hint-i" aria-hidden="true">
									ⓘ
								</span>
							</button>
						</>
					)}
					{pack.planches.map((planche, i) => (
						<PlancheView
							canDownload={canDownload}
							onPdfServer={FEATURE_DOWNLOAD_TEST ? undefined : downloadPlancheViaServer}
							key={i}
							planche={planche}
							index={i}
							items={items}
							settings={settings}
							pxPerCm={PX_PER_CM * zoom}
							dark={boardDark}
							measure={measureMode}
							selectedId={selectedId}
							hoverId={hoverId}
							onSelect={selectFromBoard}
						/>
					))}
				</main>
				{dragOver && (
					<div className="dtf-drop-overlay" aria-hidden="true">
						<span className="dtf-drop-chip">✨ {i18next.t('dtf.drop-ici')}</span>
					</div>
				)}
				{burstKey > 0 && (
					<div key={burstKey} className="dtf-sparkle-burst" aria-hidden="true">
						{Array.from({ length: 10 }, (_, i) => (
							<span key={i} className="dtf-burst-p" style={{ '--a': `${i * 36}deg` } as React.CSSProperties} />
						))}
					</div>
				)}
			</div>
			<footer className="dtf-pricebar" ref={pricebarRef}>
				{/* le textile se choisit dans la section 1 du panneau ; ici son état, cliquable pour y aller */}
				<button type="button" className={`dtf-price-cell dtf-price-textile${colle ? '' : ' is-todo'}`} onClick={() => scrollToTextile()} title={i18next.t('dtf.section-textile') as string}>
					<span className="dtf-price-label">{i18next.t('dtf.section-textile')} · {i18next.t('dtf.colle-label').toLowerCase()}</span>
					<span className="dtf-price-value dtf-price-textile-val">
						{colle ? i18next.t(colle === 'anti' ? 'dtf.textile-anti-court' : 'dtf.textile-base-court') : `${i18next.t('dtf.colle-a-choisir')} ⚠`}
					</span>
				</button>
				<div className="dtf-price-cell">
					<span className="dtf-price-label">{i18next.t('dtf.planches')}</span>
					<span className="dtf-price-value">
						<CountUp value={totalPlanchesPhysiques} format={n => String(Math.round(n))} />
					</span>
				</div>
				<div className="dtf-price-cell">
					<span className="dtf-price-label">{i18next.t('dtf.longueur-totale')}</span>
					<span className="dtf-price-value">
						<CountUp value={pack.totalCm} format={n => `${n.toFixed(1)} cm`} />
					</span>
				</div>
				<div className="dtf-price-cell">
					<span className="dtf-price-label">{i18next.t('dtf.prix-ht')}</span>
					<span className="dtf-price-value dtf-price-ht">
						<CountUp value={price.ht} format={n => fmtMoney(n, price.devise)} />
						{!colle && <span className="dtf-price-alt"> / {fmtMoney(priceAnti.ht, price.devise)}</span>}
					</span>
				</div>
				<div className="dtf-price-cell">
					<span className="dtf-price-label">{i18next.t('dtf.tva-montant', { taux: settings.tvaPct })}</span>
					<span className="dtf-price-value">
						<CountUp value={price.tva} format={n => fmtMoney(n, price.devise)} />
					</span>
				</div>
				<div className="dtf-price-cell dtf-price-main">
					<span className="dtf-price-label">{i18next.t('dtf.prix-ttc')}</span>
					<span className="dtf-price-value dtf-price-total">
						{/* jamais un total nu avant le textile (revue Neo) : « dès X » + le montant anti-sublimation */}
						{!colle && <span className="dtf-price-des">{i18next.t('dtf.des')} </span>}
						<CountUp value={price.ttcTotal} format={n => fmtMoney(n, price.devise)} />
					</span>
					{!colle && (
						<span className="dtf-price-alt-ttc">{i18next.t('dtf.ttc-anti', { prix: fmtMoney(priceAnti.ttcTotal, price.devise) })}</span>
					)}
					{price.minApplied && (
						<span className="dtf-price-min">
							{i18next.t('dtf.minimum-applique', { min: fmtMoney(colle === 'anti' ? settings.minCommande * 1.5 : settings.minCommande, price.devise) })}
						</span>
					)}
				</div>
				{/* UN seul bouton de commande (Daniel + Neo 15.10.2026) ; la raison d'un blocage est écrite sous le bouton */}
				<div className="dtf-order-wrap">
					<Button
						type="primary"
						size="large"
						className="dtf-order-btn dtf-order-btn-bottom"
						loading={orderBusy}
						aria-describedby={pack.planches.length === 0 || !colle ? 'dtf-order-reason' : undefined}
						title={i18next.t('dtf.commander-titre') as string}
						onClick={onOrder}
					>
						🛒 {i18next.t(orderBusy ? 'dtf.commander-busy' : 'dtf.commander')}
					</Button>
					{pack.planches.length === 0 ? (
						<span id="dtf-order-reason" className="dtf-order-reason">{i18next.t(orderReasonKey)}</span>
					) : !colle ? (
						<span id="dtf-order-reason" className="dtf-order-reason">{i18next.t('dtf.commander-raison-textile')}</span>
					) : null}
				</div>
			</footer>
			{/* Téléphone : trois vues de travail (Logos · Planche · Récapitulatif), une seule barre fixe (le prix) */}
			<nav className="dtf-mobile-tabs" aria-label={i18next.t('dtf.vues') as string}>
				{(['logos', 'planche', 'recap'] as const).map(v => (
					<button key={v} type="button" className={`dtf-mobile-tab${mobileView === v ? ' is-on' : ''}`} aria-pressed={mobileView === v} onClick={() => setMobileView(v)}>
						{i18next.t(`dtf.vue-${v}`)}
					</button>
				))}
			</nav>
			<div className={`dtf-assistant${assistOpen ? ' dtf-assistant-open' : ''}`}>
				{assistOpen ? (
					<div className="dtf-assistant-panel" role="dialog" aria-label={i18next.t('dtf.assistant-titre') as string}>
						<button
							type="button"
							className="dtf-assistant-close"
							aria-label={i18next.t('dtf.assistant-fermer') as string}
							onClick={() => setAssistOpen(false)}
						>
							✕
						</button>
						<TutoList />
						<Marmotte pose="point" tone="ok" size={56} className="dtf-marmotte-prix" title={i18next.t('dtf.mmt-prix-t') as string}>
							{i18next.t('dtf.mmt-prix', {
								min: settings.minPlancheCm,
								laize: settings.laizeCm,
								max: settings.maxPlancheCm,
								base: fmtMoney(settings.minCommande, settings.devise),
							})}
							<TutoLink n={9} />
						</Marmotte>
						<div className="dtf-help">
							<h4>{i18next.t('dtf.aide-titre')}</h4>
							<p>{i18next.t('dtf.aide-1')}</p>
							<p>{i18next.t('dtf.aide-2')}</p>
							<p>{i18next.t('dtf.aide-3')}</p>
							<p>{i18next.t('dtf.aide-4')}</p>
						</div>
					</div>
				) : (
					<button
						type="button"
						className="dtf-assistant-fab"
						aria-label={i18next.t('dtf.assistant-ouvrir') as string}
						onClick={() => setAssistOpen(true)}
					>
						<Marmotte pose="present" tone="info" size={64} hero typing>
							{i18next.t('dtf.mmt-vide-t')}
						</Marmotte>
					</button>
				)}
			</div>
			<TutoHost />
			{blocker && (
				<div className="dtf-lib-save-overlay is-done" role="alertdialog" aria-live="assertive" data-testid="blocker-overlay" data-kind={blocker.kind}>
					<div className="dtf-lib-save-card dtf-blocker-card">
						<Marmotte pose="point" tone="warn" size={72} title={i18next.t(`dtf.blocker-titre-${blocker.kind}`) as string}>
							{i18next.t(blocker.kind === 'colle' ? 'dtf.colle-requis' : blocker.kind === 'logo' ? 'dtf.commander-raison-logo' : blocker.kind === 'refus' ? 'dtf.commander-raison-refus' : 'dtf.commander-raison-analyse')}
						</Marmotte>
						<div className="dtf-lib-save-actions">
							<Button type="primary" data-testid="blocker-go" onClick={() => {
								closeBlocker();
								if (blocker.kind === 'logo') fileRef.current?.click();
								else if (blocker.kind === 'colle') scrollToTextile();
								else { setMobileView('logos'); setPendingScroll('logos'); }
							}}>{i18next.t(`dtf.blocker-btn-${blocker.kind}`)}</Button>
							<Button onClick={closeBlocker}>{i18next.t('dtf.fermer')}</Button>
						</div>
					</div>
				</div>
			)}
			{askName !== null && (
				<div className="dtf-lib-save-overlay is-done" role="dialog" aria-modal="true" data-testid="name-overlay">
					<div className="dtf-lib-save-card dtf-blocker-card dtf-name-card">
						<Marmotte pose="point" tone="info" size={72} title={i18next.t('dtf.nom-planche-titre') as string}>
							{i18next.t('dtf.nom-planche-aide')}
						</Marmotte>
						<Input
							size="large"
							className="dtf-name-input"
							autoFocus
							maxLength={40}
							value={askName}
							placeholder={i18next.t('dtf.nom-commande-ex') as string}
							data-testid="name-input"
							onChange={e => setAskName(e.target.value)}
							onPressEnter={() => { if (askName.trim()) { onJobName(askName.trim()); setAskName(null); void onOrder(true); } }}
						/>
						<div className="dtf-lib-save-actions">
							<Button type="primary" data-testid="name-ok" disabled={!askName.trim()} onClick={() => { onJobName(askName.trim()); setAskName(null); void onOrder(true); }}>
								{i18next.t('dtf.nom-planche-ok')}
							</Button>
							<Button data-testid="name-skip" onClick={() => { setAskName(null); void onOrder(true); }}>{i18next.t('dtf.nom-planche-sans')}</Button>
						</div>
					</div>
				</div>
			)}
			{orderCard && (
				<div className="dtf-lib-save-overlay is-saving" role="status" aria-live="polite" data-testid="order-overlay">
					<div className="dtf-lib-save-card">
						<Marmotte pose="magic" tone="info" size={64} typing title={orderCard.title}>
							{orderCard.text}
						</Marmotte>
						<div className="dtf-prep-busy-bar"><span /></div>
					</div>
				</div>
			)}
			{libSave && (
				<div className={`dtf-lib-save-overlay is-${libSave.phase}`} role="status" aria-live="polite" data-testid="lib-save-overlay">
					<div className="dtf-lib-save-card">
						{libSave.phase === 'saving' ? (
							<Marmotte pose="magic" tone="info" size={64} typing title={i18next.t(libSave.keep ? 'dtf.biblio-enregistrement' : 'dtf.biblio-enregistrement-planche') as string}>
								{i18next.t('dtf.biblio-enregistrement-aide')}
							</Marmotte>
						) : (
							<Marmotte pose="thumbs" tone="ok" size={64} title={i18next.t('dtf.biblio-garde-ok-t') as string}>
								{i18next.t('dtf.biblio-garde-ok', { name: libSave.name })}
							</Marmotte>
						)}
						{libSave.phase === 'saving' && <div className="dtf-prep-busy-bar"><span /></div>}
						{libSave.phase === 'done' && (
							<div className="dtf-lib-save-actions">
								<Button type="primary" onClick={() => { setLibSave(null); setLibOpen(true); }}>📚 {i18next.t('dtf.biblio-voir-biblio')}</Button>
								<Button onClick={() => setLibSave(null)}>{i18next.t('dtf.fermer')}</Button>
							</div>
						)}
					</div>
				</div>
			)}
			{newVersion && (
				<div className="dtf-maj-banner" role="status" data-testid="dtf-maj">
					<span>🔄 {i18next.t('dtf.maj-dispo')}</span>
					<Button size="small" type="primary" onClick={() => window.location.reload()}>
						{i18next.t('dtf.maj-recharger')}
					</Button>
				</div>
			)}
			{inspectId && (
				<QualityModal
					item={items.find(it => it.id === inspectId) || null}
					quality={quality[inspectId]}
					minDpi={MIN_DPI}
					thin={thinIds.includes(inspectId)}
					lowDpi={lowDpi[inspectId]}
					upscaling={upscaling.includes(inspectId)}
					fixingThin={fixingThin.includes(inspectId)}
					onUpscale={upscaleItem}
					onFixThin={fixThinItem}
					onBgEdit={id => {
						setInspectId(null);
						setBgEditId(id);
					}}
					onClose={() => setInspectId(null)}
				/>
			)}
			{bgEditId && (
				<BgRemoveModal
					item={items.find(it => it.id === bgEditId) || null}
					onApply={applyBgResult}
					onClose={() => setBgEditId(null)}
				/>
			)}
			{wizardId && (
				<PrepWizard
					key={wizardId}
					item={items.find(it => it.id === wizardId) || null}
					quality={quality[wizardId]}
					minDpi={MIN_DPI}
					thin={thinIds.includes(wizardId)}
					upscaling={upscaling.includes(wizardId)}
					fixingThin={fixingThin.includes(wizardId)}
					laizeCm={settings.laizeCm}
					maxPlancheCm={settings.maxPlancheCm}
					onChange={changeItem}
					onApplyBg={applyBgResult}
					onUpscale={upscaleItem}
					onSmooth={smoothItem}
					smoothing={smoothing.includes(wizardId)}
					onFixThin={fixThinItem}
					onVectorize={vectorizeItem}
					vectorizing={vectorizing.includes(wizardId)}
					metrics={logoMetrics[wizardId] || null}
					canSave={loggedIn}
					onClose={(done, meta) => {
						if (!done) message.info(i18next.t('dtf.wizard-ferme'), 5);
						else if (wizardQueue.length <= 1 && !meta?.keep) message.success(i18next.t('dtf.prep-pose-ok'), 6);
						setCardOpenId(wizardId);
						setWizardQueue(q => q.slice(1));
						// bibliothèque : jamais bloquant, le logo est déjà sur la planche ; l'enregistrement se fait en arrière-plan
						if (done && meta) void saveItemToLibrary(wizardId, meta);
					}}
				/>
			)}
			<LibraryModal open={libOpen} onClose={() => setLibOpen(false)} onAddLogo={onAddLogo} onReorder={onReorder} refreshKey={libRefresh} />
			<TextModal
				open={textOpen}
				dark={boardDark}
				editItem={items.find(it => it.id === textEditId) || null}
				settings={settings}
				onCreate={addTextEntries}
				onSaveEdit={saveTextEdit}
				onCancel={() => {
					setTextOpen(false);
					setTextEditId(null);
				}}
			/>
			{/* Test humain 15.10.2026 : le masque de la visite (z 1001) restait AU-DESSUS de l'assistant « Préparer » (z 1000) et avalait
			    ses clics. La visite se met en pause tant qu'une fenêtre est ouverte (assistant, bibliothèque, fond, texte) et reprend après. */}
			<Tour open={tourOpen && !wizardId && !libOpen && !bgEditId && !textOpen} current={tourStep} onChange={setTourStep} onClose={closeTour} steps={tourSteps} />
			<SettingsModal
				open={settingsOpen}
				settings={settings}
				onSave={s => {
					setSettings(s);
					saveSettings(s);
					setSettingsOpen(false);
				}}
				onCancel={() => setSettingsOpen(false)}
			/>
		</div>
	);
};

export default DTFEditor;
